import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Card, Input, DatePicker, Button, Form } from "antd";
import { Calendar, User } from "lucide-react";
import { Stage, Layer, Circle, Image } from "react-konva";
import SignatureCanvas from "react-signature-canvas";
import { useRef, useState, useEffect } from "react";
import dayjs from "dayjs";
import useImage from "use-image";
import Layout from '../../Layout'

export default function BeautyConsultation() {
  const { t } = useTranslation();
    const sigRef = useRef<any>(null);

    const [points, setPoints] = useState<{ x: number; y: number }[]>([]);

    const [face] = useImage("https://khothi.buso.asia/uploads/2025/09/photo_library/WZXKxW5DNl9d0eee4704d246866264109e17cf6005.PNG");

    interface Point {
        x: number;
        y: number;
    }

    const handleClick = (e: any): void => {
        const stage = e.target.getStage();
        const pos: Point = stage.getPointerPosition();

        setPoints([...points, pos]);
    };
    const [isDark, setIsDark] = useState(
        document.documentElement.classList.contains("dark")
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains("dark"));
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        return () => observer.disconnect();
    }, []);

    return (
        <Layout>
            <Helmet>
                <title>{t('page.beauty_consultation')} | Khothi</title>
            </Helmet>
            <div className="flex justify-between items-center">

                <div className="flex gap-3 items-center">
                    <div className="bg-emerald-600 text-white p-2 rounded">
                        <User size={18} />
                    </div>

                    <div>
                        <div className="font-semibold text-lg text-000000e0">
                            {t('beauty_consultation.page_title')}
                        </div>

                        <div className="text-sm text-gray-500">
                            {t('beauty_consultation.page_subtitle')}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <DatePicker
                        defaultValue={dayjs()}
                        format="DD/MM/YYYY"
                        className="min-w-[160px]"
                    />
                </div>

            </div>

            {/* CONTENT */}

            <div className="mx-auto py-8 grid grid-cols-12 gap-6">

                {/* LEFT */}

                <div className="col-span-12 lg:col-span-8">

                    <Card>

                        <div className="font-semibold mb-4 flex items-center gap-2 text-000000e0">
                            {t('beauty_consultation.customer_info')}
                        </div>

                        <Form layout="vertical">

                            <Form.Item label={t('beauty_consultation.customer_name')}>
                                <Input />
                            </Form.Item>

                            <div className="grid grid-cols-2 gap-4">

                                <Form.Item label={t('beauty_consultation.dob')}>
                                    <DatePicker className="w-full" />
                                </Form.Item>

                                <Form.Item label={t('beauty_consultation.phone')}>
                                    <Input />
                                </Form.Item>

                            </div>

                        </Form>

                    </Card>

                    <Card className="mt-6">

                        <div className="font-semibold mb-4 text-000000e0">
                            {t('beauty_consultation.consultation_content')}
                        </div>

                        <Form layout="vertical">

                            <Form.Item label={t('beauty_consultation.customer_needs')}>
                                <Input.TextArea rows={3} />
                            </Form.Item>

                            <Form.Item label={t('beauty_consultation.current_status')}>
                                <Input.TextArea rows={3} />
                            </Form.Item>

                            <Form.Item label={t('beauty_consultation.diagnosis')}>
                                <Input.TextArea rows={3} />
                            </Form.Item>

                            <Form.Item label={t('beauty_consultation.treatment_direction')}>
                                <Input.TextArea rows={3} />
                            </Form.Item>

                            <Form.Item label={t('beauty_consultation.note')}>
                                <Input.TextArea rows={2} />
                            </Form.Item>

                        </Form>

                        <div className="flex justify-end gap-3 mt-4">
                            <Button>{t('beauty_consultation.print')}</Button>
                            <Button type="primary">{t('beauty_consultation.save')}</Button>
                        </div>

                    </Card>

                </div>

                {/* RIGHT */}

                <div className="col-span-12 lg:col-span-4">

                    <div className="sticky top-10 space-y-6">

                        {/* FACE MAPPING */}

                        <Card>

                            <div className="text-center font-semibold mb-4 text-000000e0">
                                {t('beauty_consultation.face_map')}
                            </div>

                            <div className="border border-dashed border-emerald-300 rounded flex justify-center">

                                <Stage
                                    width={220}
                                    height={280}
                                    onClick={handleClick}
                                >
                                    <Layer>

                                        {/* IMAGE FACE */}

                                        <Image
                                            image={face}
                                            width={220}
                                            height={280}
                                        />

                                        {/* MARKERS */}

                                        {points.map((p, i) => (
                                            <Circle
                                                key={i}
                                                x={p.x}
                                                y={p.y}
                                                radius={5}
                                                fill="red"
                                                onClick={(e) => {
                                                    e.cancelBubble = true;

                                                    const newPoints = points.filter((_, index) => index !== i);
                                                    setPoints(newPoints);
                                                }}
                                            />
                                        ))}

                                    </Layer>
                                </Stage>

                            </div>

                            <div className="text-center text-xs text-gray-500 mt-2">
                                {t('beauty_consultation.face_click_mark')}
                            </div>
                            <div className="text-center text-xs text-gray-500 mt-2">
                                {t('beauty_consultation.face_click_remove')}
                            </div>
                            <div className="flex justify-center mt-2">
                                <Button
                                    onClick={() => setPoints([])}
                                >
                                    {t('beauty_consultation.clear_all')}
                                </Button>
                            </div>
                        </Card>

                        {/* SIGNATURE */}

                        <Card>

                            <div className="text-center font-medium mb-3 text-000000e0">
                                {t('beauty_consultation.doctor_signature')}
                            </div>

                            <SignatureCanvas
                                key={isDark ? "dark" : "light"}
                                ref={sigRef}
                                penColor={isDark ? "#ffffff" : "#000000"}
                                canvasProps={{
                                    className:
                                        "border rounded w-full h-[120px] bg-white dark:bg-slate-900",
                                }}
                            />

                            <Button
                                className="mt-3 w-full"
                                onClick={() => sigRef.current.clear()}
                            >
                                {t('beauty_consultation.clear_signature')}
                            </Button>

                        </Card>

                    </div>

                </div>

            </div>
        </Layout>
    );
}